import axiosClient from '@/api/axiosClient';
import { isAxiosError } from 'axios';
import { asContainerIdOrNull, type ContainerId } from '@/types/brandedIds';

export class ExtractionInProgressError extends Error {
  constructor (public readonly uploadId: string) {
    super(`Excel-ekstraksjon pågår for upload ${uploadId}`);
    this.name = 'ExtractionInProgressError';
  }
}

export interface FilePreviewMetadata {
  separator?: string;
  escape?: string;
  quote?: string;
  encoding?: string;
  headerRow?: number;
  firstDataRow?: number;
}

/**
 * Signaliserer at en kolonne i kildefilen (Excel) har en påheftet celle-kommentar
 * (Excels «gule note»). Kommentaren ignoreres ved import; dette er kun et preview-signal.
 * `columnIndex` er 0-basert relativt til arkets venstre datakant og matcher kolonne-
 * rekkefølgen i `previewRows` / `headerRow`.
 */
export interface CellCommentInfo {
  columnIndex: number;
  author: string;
  text: string;
}

export interface FilePreviewResponse {
  uploadId: string;
  fileName?: string;
  metadata?: FilePreviewMetadata;
  headerRow?: string[];
  previewRows?: string[][];
  sheets?: Record<string, string>;
  cellComments?: CellCommentInfo[];
  isTextFile?: boolean;
  hasError?: boolean;
  errorMessage?: string;
}

export interface StartFileAnalyseRequest {
  uploadId: string;
  analysisLines: number;
  /**
   * Offentlig tabellnøkkel når den er kjent (f.eks. importveiviser med eksisterende tabell).
   * Utelates ved analyse før mål-tabell er valgt (f.eks. «opprett importmal fra fil»).
   */
  tableKey?: string | null;
  importDefinitionKey: string | null;
}

export interface StartFileAnalyseResponse {
  jobId: string;
}

/**
 * WI 2318 (design-of-record §3): preview tar `ContainerId` — den opplastede
 * arbeidsboka. Ren stateless inspeksjon; BE resolverer arket og setter ingenting.
 * Branded param hindrer at en `DatasetId` (resolvert ark) feilaktig sendes hit.
 */
export async function getFilePreviewApi (
  uploadId: ContainerId,
  previewLines: number,
  signal?: AbortSignal
): Promise<FilePreviewResponse> {
  try {
    const response = await axiosClient.get<FilePreviewResponse>('v2/preview-file', {
      params: { uploadId, previewLines },
      signal
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 409 && error.response.data?.type === 'extraction-in-progress') {
      const beUploadId = (error.response.data as { uploadId?: string })?.uploadId;
      if (!beUploadId) {
        console.error('[filePreview] BE 409 extraction-in-progress mangler uploadId i payload', error.response.data);
        throw error;
      }
      throw new ExtractionInProgressError(beUploadId);
    }
    throw error;
  }
}

export async function startFileAnalyseApi (
  request: StartFileAnalyseRequest,
  signal?: AbortSignal
): Promise<StartFileAnalyseResponse> {
  const response = await axiosClient.post<StartFileAnalyseResponse>('v2/file-analyse/start', request, { signal });
  return response.data;
}

export async function getFileAnalyseResultApi (jobId: string, signal?: AbortSignal): Promise<any> {
  const response = await axiosClient.get(`v2/file-analyse/${jobId}`, { timeout: 30000, signal });
  return response.data;
}
